import {
  Button,
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';

import { Dropdown } from '~components/Dropdown/Dropdown';
import { DropdownMods } from '~components/Dropdown/Dropdown.types';
import { FormCheckbox } from '~components/Forms/FormCheckbox';
import { FormField } from '~components/Forms/FormField';
import { FormInput } from '~components/Forms/FormInput';
import { Markdown } from '~components/Markdown';
import { NextImage } from '~components/NextImage/NextImage';
import { PreloaderOverlay } from '~components/PreloaderOverlay/PreloaderOverlay';
import { Validation } from '~helpers/forms/Validation';
import { getUtmFromUrl } from '~helpers/getUtmFromUrl';
import { StringUtils } from '~helpers/StringUtils';
import { useForm, UseFormField } from '~hooks/useForm';
import { EventsApi } from '~services/EventsApi';
import { EventsApiRegistrationRequest } from '~services/EventsApi/types';
import { Goals } from '~services/Goals';

import styles from './EventRegistrationForm.module.scss';
import { EventRegistrationFormProps } from './EventRegistrationForm.types';

function getTextByValue(value: string) {
  switch (value) {
    case 'online':
      return 'Онлайн';
    case 'offline':
      return 'Оффлайн';
    default:
      return value;
  }
}

export const EventRegistrationForm = ({
  title,
  text,
  event,
  fields,
  goals,
  success = {
    title: 'Спасибо за регистрацию',
    text: 'В течение 3-ех дней заявка будет обработана. После рассмотрения вы получите email на почту.',
  },
}: EventRegistrationFormProps) => {
  const formFields: Record<string, UseFormField> = {
    name: {
      value: '',
      validate: Validation.required(
        Validation.cyrillic('Введите имя на русском'),
      ),
    },
    surname: {
      value: '',
      validate: Validation.required(
        Validation.cyrillic('Введите фамилию на русском'),
      ),
    },
    email: {
      value: '',
      validate: Validation.required(Validation.email()),
    },
    phone: {
      value: '',
      validate: Validation.required(Validation.phone()),
    },
    company: {
      value: '',
      validate: Validation.required(),
    },
    companyLevel: {
      value: '',
      validate: Validation.required(),
    },
    privacy: {
      value: true,
      validate: Validation.privacy(),
    },
    format: {
      validate: Validation.required(),
      value: event.formats.length > 1 ? '' : event.formats.toString(),
    },
  };
  fields?.inputs?.forEach((field) => {
    formFields[field.name] = {
      value: '',
      validate: field.required ? Validation.required() : undefined,
    };
  });
  fields?.privacy?.forEach((field) => {
    formFields[field.name] = {
      value: Boolean(field.required),
      validate: field.required
        ? Validation.privacy('Обязательное поле')
        : undefined,
    };
  });
  fields?.options?.forEach((field) => {
    formFields[field.name] = {
      value: [],
      validate: field.required
        ? Validation.array(1, 'Обязательное поле')
        : undefined,
    };
  });
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    status,
    setStatus,
    setValues,
    resetForm,
    isSubmitting,
    touched,
    setTouched,
  } = useForm({
    fields: formFields,
    submit(data) {
      const utm = getUtmFromUrl();
      const extra: EventsApiRegistrationRequest['extra'] = [];

      fields?.inputs?.forEach((field) => {
        extra.push({
          key: field.label,
          value: data[field.name],
        });
      });
      fields?.options?.forEach((option) => {
        const value = data[option.name];

        extra.push({
          key: option.title,
          value: Array.isArray(value) ? value.join('\n') : value,
        });
      });

      EventsApi.registration({
        extra,
        utm: {
          campaign: utm?.utm_campaign,
          content: utm?.utm_content,
          medium: utm?.utm_medium,
          source: utm?.utm_source,
          term: utm?.utm_term,
        },
        eventDocId: event.documentId,
        format: data.format.toUpperCase(),
        member: {
          company: data.company,
          positionAtWork: data.companyLevel,
          email: data.email,
          phone: StringUtils.replaceToNumbers(data.phone),
          name: data.name,
          surname: data.surname,
        },
      })
        .then(() => {
          if (goals?.ym) {
            Goals.ym(goals.ym);
          }

          if (goals?.ga) {
            Goals.gtag4(goals.ga);
          }

          // обязательная цель после регистрации
          Goals.tmr({
            goal: 'registration-events',
          });

          setStatus({
            success: {
              title: success?.title,
              message: success?.text,
            },
          });
        })
        .catch((error) => {
          setStatus({
            error: {
              message: error.message,
            },
          });
        });
    },
  });

  function handleFormatChange(value: string) {
    setValues((prevState) => ({
      ...prevState,
      format: value,
    }));
  }

  function handleResetClick() {
    return resetForm();
  }

  if (status.success) {
    return (
      <div className={classNames(styles.wrapper, styles.status)}>
        <div className={styles.successImage}>
          <NextImage
            src={`${process.env.STATIC}/images/eventsRegistrationSuccess.png`}
            alt="success_image"
          />
        </div>
        <Typography
          tagName={TypographySizes.h3}
          mods={[TypographyMods.titleL]}
          className={styles.successTitle}
        >
          {status.success.title}
        </Typography>
        <Typography
          tagName={TypographySizes.p}
          mods={[TypographyMods.bodyM]}
          className={styles.statusText}
        >
          {status.success.message}
        </Typography>
        <div className={styles.statusButton}>
          <Button
            className={styles.buttonReset}
            mod="secondary"
            onClick={handleResetClick}
            type="reset"
            icon={`${process.env.STATIC}/images/icons/replay_outline_28.svg`}
          >
            Вернуться к регистрации
          </Button>
        </div>
      </div>
    );
  }

  if (status.error) {
    return (
      <div className={classNames(styles.wrapper, styles.status)}>
        <div className={classNames(styles.statusText, styles.errorText)}>
          {status.error.message}
        </div>
        <div className={styles.statusButton}>
          <Button
            className={styles.buttonReset}
            mod="secondary"
            onClick={handleResetClick}
            type="reset"
            icon={`${process.env.STATIC}/images/icons/replay_outline_28.svg`}
          >
            Вернуться к регистрации
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PreloaderOverlay active={isSubmitting} />
      <div className={styles.wrapper}>
        {(title || text) && (
          <div className={styles.head}>
            {title && (
              <Typography
                tagName={TypographySizes.h3}
                mods={[TypographyMods.titleL]}
                className={styles.title}
              >
                {title}
              </Typography>
            )}
            {text && <div className={styles.text}>{text}</div>}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.fields}>
            <FormField
              errors={touched.email && errors.email}
              label="Электронная почта*"
            >
              <FormInput
                name="email"
                type="email"
                value={values.email}
                isError={Boolean(errors.email && touched.email)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <FormField errors={touched.name && errors.name} label="Имя*">
              <FormInput
                name="name"
                value={values.name}
                isError={Boolean(errors.name && touched.name)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <FormField
              errors={touched.surname && errors.surname}
              label="Фамилия*"
            >
              <FormInput
                name="surname"
                value={values.surname}
                isError={Boolean(errors.surname && touched.surname)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <FormField errors={touched.phone && errors.phone} label="Телефон*">
              <FormInput
                name="phone"
                type="tel"
                value={values.phone}
                isError={Boolean(errors.phone && touched.phone)}
                onBlur={handleBlur}
                onChange={handleChange}
                mask="+7 (999) 999-99-99"
              />
            </FormField>
            <FormField
              errors={touched.companyLevel && errors.companyLevel}
              label="Должность*"
            >
              <FormInput
                name="companyLevel"
                value={values.companyLevel}
                isError={Boolean(errors.companyLevel && touched.companyLevel)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <FormField
              errors={touched.company && errors.company}
              label="Компания*"
            >
              <FormInput
                name="company"
                value={values.company}
                isError={Boolean(errors.company && touched.company)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            {event.formats.length >= 1 && (
              <FormField
                errors={touched.format ? errors.format : undefined}
                label="Выберите формат участия*"
                dataTestId="eventFormat"
              >
                <Dropdown
                  mods={
                    touched.format && errors.format
                      ? [DropdownMods.Error]
                      : undefined
                  }
                  onChange={handleFormatChange}
                  onBlur={() => setTouched({ format: true })}
                  value={values.format}
                  disabled={event.formats.length === 1}
                  values={[
                    {
                      value: '',
                      text: 'Выбрать',
                      placeholder: true,
                    },
                    ...event.formats.map((value) => ({
                      value,
                      text: getTextByValue(value),
                    })),
                  ]}
                />
              </FormField>
            )}
            {fields?.inputs?.map((field) => (
              <FormField
                errors={touched[field.name] && errors[field.name]}
                label={field.label}
                key={field.name}
                dataTestId="eventExtraInput"
              >
                <FormInput
                  name={field.name}
                  type="text"
                  value={values[field.name]}
                  isError={Boolean(errors[field.name] && touched[field.name])}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </FormField>
            ))}
          </div>
          {fields?.options && (
            <div className={styles.options}>
              {fields.options.map((fieldset) => {
                return (
                  <FormField
                    errors={touched[fieldset.name] && errors[fieldset.name]}
                    key={fieldset.name}
                    dataTestId="eventOptions"
                  >
                    <fieldset name={fieldset.name} className={styles.fieldset}>
                      <div className={styles.fieldsetTitle}>
                        {fieldset.title}
                      </div>
                      <div className={styles.fieldsetOptions}>
                        {fieldset?.options?.map((option) => {
                          const optionValue = option.label;
                          const optionChecked =
                            values[fieldset.name].includes(optionValue);

                          return (
                            <label
                              key={option.id}
                              className={classNames(styles.option, {
                                [styles.checked]: optionChecked,
                                [styles.error]: Boolean(
                                  errors[fieldset.name] &&
                                    touched[fieldset.name],
                                ),
                              })}
                              onClick={() =>
                                setTouched({ [fieldset.name]: true })
                              }
                            >
                              <input
                                type="checkbox"
                                name={fieldset.name}
                                onChange={handleChange}
                                value={optionValue}
                                checked={optionChecked}
                              />
                              {option.label}
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  </FormField>
                );
              })}
            </div>
          )}
          <div className={styles.footer}>
            <div className={styles.privacy}>
              <FormField errors={touched.privacy && errors.privacy}>
                <FormCheckbox
                  name="privacy"
                  value={values.privacy}
                  checked={!!values.privacy}
                  isError={Boolean(errors.privacy && touched.privacy)}
                  onChange={handleChange}
                />
              </FormField>
              {fields?.privacy?.map((field) => (
                <FormField
                  errors={errors[field.name]}
                  key={field.name}
                  dataTestId="eventExtraPrivacy"
                >
                  <FormCheckbox
                    name={field.name}
                    value={values[field.name]}
                    checked={!!values[field.name]}
                    isError={!!errors[field.name]}
                    onChange={handleChange}
                  >
                    <Markdown markdownString={field.text} />
                  </FormCheckbox>
                </FormField>
              ))}
            </div>
            <div className={styles.submit}>
              <Button
                type="submit"
                mod="primary"
                className={styles.buttonSubmit}
              >
                Зарегистрироваться
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};
